/// <reference path="koyori.ts" />
/// <reference path="fetch.ts" />
/// <reference path="checkbox.ts" />
/// <reference path="animation.ts" />

/**
 * 直下にあるテーブルをデータグリッドにするコンポーネントです。
 * <dl>
 * <dt>属性</dt>
 * <dd>url: データ取得先URL</dd>
 * <dd>count-name: 行数のレスポンスパラメータ名（省略時: count）</dd>
 * <dd>rows-name: 行データのレスポンスパラメータ名（省略時: rows）</dd>
 * <dd>sort-name: ソート対象のリクエストパラメータ名（省略時: sort）</dd>
 * <dd>order-name: ソート順序のリクエストパラメータ名（省略時: order）</dd>
 * <dd>page-name: 開始ページのリクエストパラメータ名（省略時: page）</dd>
 * <dd>size-name: 取得件数のリクエストパラメータ名（省略時: size）</dd>
 * <dd>sort-column: ソート対象のカラム</dd>
 * <dd>sort-order: ソート順序（asc, desc）</dd>
 * <dd>selectable: 行選択可能にします</dd>
 * </dl>
 * <dl>
 * <dt>イベント</dt>
 * <dd>fetch-error: AJAX通信でエラーが発生した場合</dd>
 * <dd>row-append: 行が追加された場合</dd>
 * <dd>check: チェックボックのチェック状態が変更された場合</dd>
 * </dl>
 */
class KoyoriDatagrid extends KoyoriElement {

	private table: HTMLTableElement;

	private thead: HTMLTableSectionElement;

	private tbody: HTMLTableSectionElement;

	private tfoot: HTMLTableSectionElement;

	/** 最大行数 */
	private maxRows: number;

	/** AJAX通信中ならtrue */
	private fetching = false;

	/** 一度に取得する行数 */
	private limit = 50;

	/** 最後に読み込んだページ番号 */
	private lastReadPage = -1;

	/** リサイズ用タイマ */
	private resizeTimer: NodeJS.Timer;

	/** スクロール用タイマ */
	private scrollTimer: NodeJS.Timer;

	/** 選択されている行リスト */
	private selectedRows: HTMLElement[];

	/** 最後にレイアウトを行った時点のclientWidth */
	private lastClientWidth: number;

	constructor() {
		super();
	}

	connectedCallback(): void {
		if (this.table == undefined) {
			this.table = document.createElement('table');

			this.thead = document.createElement('thead');
			this.tfoot = document.createElement('tfoot');
			let theadTr = document.createElement('tr');
			this.thead.appendChild(theadTr);
			this.table.appendChild(this.thead);

			{
				let th = document.createElement('th');
				th.classList.add('koy-datagrid-button', 'koy-datagrid-checkbox');
				let checkbox = new KoyoriCheckbox();
				checkbox.setAttribute('name', 'all');
				th.appendChild(checkbox);
				theadTr.appendChild(th);
			}
			KoyoriElement.querySelectorAll(this, "koy-datagrid-column").forEach((column) => {
				let th = document.createElement('th');
				th.setAttribute('data-name', column.getAttribute('name'));
				let label = column.querySelector("koy-datagrid-label");
				if (label != null) {
					th.appendChild(label.cloneNode(true));
				} else {
					th.textContent = column.getAttribute('label') || '';
				}
				th.classList.add('koy-datagrid-' + (column.getAttribute('type') || 'text'));
				if (column.getAttribute('header-class') != null) {
					column.getAttribute('header-class').split(' ').forEach(part => {
						th.classList.add(part);
					});
				}
				if (column.getAttribute('sortable') != null) {
					th.classList.add('koy-datagrid-sortable');
					th.addEventListener('click', (event) => { this.headClicked(event); });
				}
				theadTr.appendChild(th);
			});

			this.tbody = document.createElement('tbody');
			this.table.appendChild(this.tbody);

			this.tfoot = document.createElement('tfoot');
			let tfootTr = document.createElement('tr');
			let tfootTh = document.createElement('th');
			tfootTh.setAttribute('colspan', String(this.querySelectorAll('koy-datagrid-column').length));
			tfootTr.appendChild(tfootTh);
			this.tfoot.appendChild(tfootTr);
			this.table.appendChild(this.tfoot);

			this.appendChild(this.table);

			this.addEventListener('change', event => {
				let name = (<HTMLElement>event.target).getAttribute('name');
				if (name == 'all') {
					let all = <KoyoriCheckbox>this.querySelector('.koy-datagrid-checkbox koy-checkbox[name="all"]');
					KoyoriElement.querySelectorAll(this.tbody, '.koy-datagrid-checkbox koy-checkbox[name="row"]').forEach(row => {
						(<KoyoriCheckbox>row).checked = all.checked;
					});
				} else {
					this.updateCheckboxAll();
				}
				this.fireCheckEvent();
			});
		}

		super.connectedCallback();

		setTimeout(() => {
			this.bodyScrolled();
		});
		this.tbody.addEventListener('scroll', event => {
			if (this.scrollTimer != undefined) {
				clearTimeout(this.scrollTimer);
			}
			this.scrollTimer = setTimeout(() => {
				this.bodyScrolled();
				this.scrollTimer = undefined;
			}, 10);
		});
		window.addEventListener("resize", () => {
			if (this.resizeTimer != undefined) {
				clearTimeout(this.resizeTimer);
			}
			this.resizeTimer = setTimeout(() => {
				this.reLayout(true)
				this.resizeTimer = undefined;
			}, 100);
		});
	}

	/**
	 * 必要なレコードをAJAX通信で取得してテーブルに格納します。
	 */
	readNextData(): void {
		let currentRows = this.tbody.getElementsByTagName("tr").length;
		if (this.maxRows != undefined && this.maxRows <= currentRows) {
			return;
		}

		if (this.fetching) {
			return;
		}
		this.fetching = true;

		let focus = this.querySelector(':focus')
		if (focus != null) {
			(<HTMLElement>focus).blur();
		}

		let params = new URLSearchParams();
		let page = currentRows / this.limit;
		if (page <= this.lastReadPage) {
			this.fetching = false;
			return;
		}
		params.append(this.getAttribute('page-name') || 'page', String(page));
		params.append(this.getAttribute('size-name') || 'size', String(this.limit));
		if (this.getAttribute('sort-column') != null) {
			params.append(this.getAttribute('sort-name') || 'sort', this.getAttribute('sort-column'));
		}
		if (this.getAttribute('sort-order') != null) {
			params.append(this.getAttribute('order-name') || 'order', this.getAttribute('sort-order'));
		}
		let url = this.getAttribute('url');
		if (url.includes('?')) {
			url += '&';
		} else {
			url += '?';
		}
		KoyoriFetch.fetch(url + params.toString(), {
			credentials: 'include'
		}, {
			success: response => {
				this.lastReadPage = page;
				response.json().then(data => {
					if (data == null) {
						return;
					}
					this.maxRows = data[this.getAttribute('count-name') || 'count'];
					let rows: any[] = data[this.getAttribute('rows-name') || 'rows'];
					let columns = KoyoriElement.querySelectorAll(this, "koy-datagrid-column");
					let trs: HTMLElement[] = [];
					rows.forEach((row, index) => {
						let tr = document.createElement('tr');
						{
							let td = document.createElement('td');
							td.classList.add('koy-datagrid-button', 'koy-datagrid-checkbox');
							let checkbox = new KoyoriCheckbox();
							checkbox.setAttribute('name', 'row');
							td.appendChild(checkbox);
							tr.appendChild(td);
						}
						this.renderRowColumns(tr, row, columns);
						tr.setAttribute("data-index", String(currentRows + index));
						tr.setAttribute("data-row", JSON.stringify(row));
						this.tbody.appendChild(tr);
						trs.push(tr);
					});
					this.reLayout(true);
					this.bodyScrolled();
					if (trs.length > 0) {
						this.updateCheckboxAll();
					}
					KoyoriElement.fireEvent(this, 'row-append', true, true, trs);
				});
				return true;
			},
			complete: () => {
				this.fetching = false;
				return true;
			}
		}, this);
	}

	/**
	 * 表のレイアウトを調整します。
	 * 
	 * @param updateWidth エレメントの幅を更新する場合はtrue
	 */
	reLayout(updateWidth: boolean = false): void {
		let scrollWidth = KoyoriElement.getScrollBarSize();

		if (updateWidth || this.lastClientWidth != this.tbody.clientWidth) {
			this.lastClientWidth = this.tbody.clientWidth;

			this.style.width = '';
			this.thead.style.display = '';
			this.tbody.style.display = '';
			
			this.style.width = (this.clientWidth - scrollWidth) + 'px';

			let widthList: number[] = new Array();
			let ths = KoyoriElement.querySelectorAll(this.thead, 'th');
			let tds = KoyoriElement.querySelectorAll(this.tbody, 'tr:first-child td');
			ths.forEach(th => {
				th.style.minWidth = '';
			});
			tds.forEach(td => {
				td.style.minWidth = '';
			});
			tds.forEach(th => {
				widthList.push(th.offsetWidth);
			});
			tds.forEach((td, index) => {
				td.style.minWidth = widthList[index] + 'px';
			});
			ths.forEach((th, index) => {
				th.style.minWidth = widthList[index] + 'px';
			});

			let currentRows = this.tbody.getElementsByTagName("tr").length;
			if (currentRows > 0) {
				this.thead.style.display = 'block';
				this.tbody.style.display = 'block';
			}
		}

		let parent = this.parentElement;
		parent.style.overflowY = 'hidden';

		let height = KoyoriElement.getFitHeight(this) - this.thead.offsetHeight - this.tfoot.offsetHeight;
		this.tbody.style.height = height + "px";
	}

	/**
	 * @return 選択中の行のリスト
	 */
	getSelectedRows(): HTMLElement[] {
		return this.selectedRows;
	}

	/**
	 * データを初期化して再度読み込みます。
	 */
	refresh(): void {
		this.tbody.innerHTML = '';
		this.selectedRows = [];
		this.maxRows = undefined;
		this.lastReadPage = -1;
		this.readNextData();
	}

	/**
	 * 指定された行を削除します。
	 * 
	 * @param rows trエレメントのリスト
	 */
	deleteRows(rows: HTMLElement[]): void {
		let needEvent = false;
		rows.forEach(row => {
			KoyoriAnimation.fadeOut(row);
			setTimeout(() => {
				this.tbody.removeChild(row);
			}, 300);
			if (needEvent == false && rows.indexOf(row) >= 0) {
				needEvent = true;
			}
		});
		setTimeout(() => {
			this.maxRows -= rows.length;
			if (needEvent) {
				this.fireCheckEvent();
			}
			this.reLayout();
			this.updateCheckboxAll();
			this.bodyScrolled();
		}, 300);
	}

	/**
	 * 指定された行のデータセル（チェックボックス列を除く）を生成して追加します。
	 *
	 * @param tr 対象の行
	 * @param row 行データ
	 * @param columns カラム定義のリスト
	 */
	private renderRowColumns(tr: HTMLElement, row: any, columns: HTMLElement[]): void {
		columns.forEach((column) => {
			let name = column.getAttribute('name');
			let td = document.createElement('td');
			td.classList.add('koy-datagrid-' + (column.getAttribute('type') || 'text'));
			if (column.getAttribute('data-class') != null) {
				column.getAttribute('data-class').split(' ').forEach(part => {
					td.classList.add(part);
				});
			}
			let content = column.querySelector("koy-datagrid-content");
			if (content != null) {
				td.appendChild(content.cloneNode(true));
			} else {
				let labels = JSON.parse(column.getAttribute('labels') || '{}');
				let text = this.getData(row, name);
				if (text == undefined || text == null) {
					text = '';
				}
				if (labels[text] != undefined) {
					text = labels[text];
				}
				td.setAttribute('title', text);
				td.textContent = text;
			}
			tr.appendChild(td);
		});
	}

	/**
	 * 指定された行を再検索せずにその場で更新します。
	 * 全件取得（refresh）と異なりスクロール位置を維持します。
	 *
	 * @param tr 対象の行
	 * @param row 新しい行データ
	 */
	updateRow(tr: HTMLElement, row: any): void {
		let columns = KoyoriElement.querySelectorAll(this, "koy-datagrid-column");
		// チェックボックス列（先頭td）を残してデータセルを作り直す
		while (tr.children.length > 1) {
			tr.removeChild(tr.lastElementChild);
		}
		this.renderRowColumns(tr, row, columns);
		tr.setAttribute("data-row", JSON.stringify(row));
		this.reLayout(true);
		// 行装飾を再適用するため対象行でrow-appendを再発火する
		KoyoriElement.fireEvent(this, 'row-append', true, true, [tr]);
	}

	/**
	 * ヘッダがクリックされた際に呼び出されます。
	 *
	 * @param event
	 */
	private headClicked(event: MouseEvent): void {
		let target = <HTMLElement>event.target;

		let name = target.getAttribute('data-name');

		let order: string;
		if (target.classList.contains('koy-datagrid-asc')) {
			order = 'desc';
		} else if (target.classList.contains('koy-datagrid-desc')) {
			order = '';
			name = '';
		} else {
			order = 'asc';
		}

		KoyoriElement.querySelectorAll(this.thead, 'th').forEach(th => {
			th.classList.remove('koy-datagrid-asc');
			th.classList.remove('koy-datagrid-desc');
		});

		this.setAttribute('sort-column', name);
		this.setAttribute('sort-order', order);

		if (order != '') {
			target.classList.add('koy-datagrid-' + order);
		}

		this.refresh();
	}

	/**
	 * ボディがスクロールされた際に呼び出されます。
	 * 
	 * @param event
	 */
	private bodyScrolled(): void {
		let lastRow = this.tbody.querySelector('tr:last-child');
		let lastTop = 0;
		if (lastRow != null) {
			lastTop = lastRow.getBoundingClientRect().top;
		}
		if (lastTop == 0 || lastTop < this.tbody.clientHeight + lastRow.clientHeight * (this.limit / 2)) {
			this.readNextData();
		}

		if (this.tbody.scrollTop == 0) {
			this.classList.remove('koy-datagrid-scrolled');
		} else {
			this.classList.add('koy-datagrid-scrolled');
		}

		this.thead.style.left = "-" + this.tbody.scrollLeft + "px";
		this.tfoot.style.left = "-" + this.tbody.scrollLeft + "px";

		let tr = this.tbody.querySelector('tr');
		let halfRowHeight = 0;
		if (tr != null) {
			halfRowHeight = tr.offsetHeight / 2;
		}

		let clientRect = this.tbody.getBoundingClientRect();
		let tbodyLeft = clientRect.left;
		let tbodyTop = clientRect.top
		let tbodyBottom = tbodyTop + KoyoriElement.getOuterHeight(this.tbody);

		let topRow = -1;
		let topElement = document.elementFromPoint(tbodyLeft + halfRowHeight, tbodyTop + halfRowHeight);
		if (topElement != null) {
			let topTr = topElement.closest('tr');
			if (topTr != null && topTr.getAttribute('data-index') != null) {
				topRow = parseInt(topTr.getAttribute('data-index'));
			}
		}

		let bottomRow = -1;
		let bottomElement = document.elementFromPoint(tbodyLeft + halfRowHeight, tbodyBottom - halfRowHeight);
		if (bottomElement != null) {
			let bottomTr = bottomElement.closest('tr');
			if (bottomTr == null) {
				bottomTr = this.tbody.querySelector('tr:last-child');
			}
			if (bottomTr != null && bottomTr.getAttribute('data-index') != null) {
				bottomRow = parseInt(bottomTr.getAttribute('data-index'));
			}
		}

		let footTh = this.tfoot.querySelector('th');
		if (topRow < 0 && bottomRow < 0) {
			footTh.style.textAlign = 'center';
			footTh.textContent = 'データがありません';
		} else {
			footTh.style.textAlign = '';
			footTh.textContent = (topRow + 1) + '-' + (bottomRow + 1) + ' / ' + this.maxRows;
		}
	}

	/**
	 * 全選択のチェックボックスの状態を更新します。
	 */
	private updateCheckboxAll(): void {
		let checked: boolean;
		let indeterminate = false;
		KoyoriElement.querySelectorAll(this.tbody, '.koy-datagrid-checkbox koy-checkbox[name="row"]').forEach(row => {
			if (indeterminate) {
				return;
			}
			if (checked == undefined) {
				checked = (<KoyoriCheckbox>row).checked;
			} else if (checked != (<KoyoriCheckbox>row).checked) {
				indeterminate = true;
			}
		});
		let all = <KoyoriCheckbox>this.querySelector('.koy-datagrid-checkbox koy-checkbox[name="all"]');
		all.indeterminate = indeterminate;
		all.checked = checked;
	}

	/**
	 * checkイベントを発行します。
	 */
	private fireCheckEvent(): void {
		this.selectedRows = [];
		KoyoriElement.querySelectorAll(this.tbody, '.koy-datagrid-checkbox koy-checkbox[name="row"]').forEach(row => {
			if ((<KoyoriCheckbox>row).checked) {
				this.selectedRows.push(<HTMLElement>row.closest('tr'));
			}
		});
		KoyoriElement.fireEvent(this, 'check', true, true, this.selectedRows);
	}

	/**
	 * @param row
	 * @param name
	 * @return 値
	 */
	private getData(row: any, name: string): any {
		if (row == undefined) {
			return null;
		}
		let point = name.indexOf(".");
		if (point > 0) {
			return this.getData(row[name.substring(0, point)], name.substring(point + 1));
		}
		return row[name];
	}
}
window.customElements.define('koy-datagrid', KoyoriDatagrid);

/**
 * カラムの情報を設定します。
 * コンテンツに内容が設定されている場合は、その内容がセルにコピーされます。
 * <dl>
 * <dt>属性</dt>
 * <dd>label: カラムの表示名</dd>
 * <dd>name: カラムのパラメータ名</dd>
 * <dd>sortable: ソートの可否</dd>
 * <dd>type: カラムの種別（text, number, date, time, datetime, button) （デフォルト: text）</dd>
 * <dd>labels: カラムの値と置き換える文字列定義のJSON （例: { "key1": "label1", "key2": "label2" }）</dd>
 * <dd>header-class: thエレメントに付与するクラス</dd>
 * <dd>data-class: tdエレメントに付与するクラス</dd>
 * </dl>
 */
class KoyoriDatagridColumn extends KoyoriElement {
}
window.customElements.define('koy-datagrid-column', KoyoriDatagridColumn);

/**
 * ラベルに文字列以外を表示する場合に指定します。
 */
class KoyoriDatagridLabel extends KoyoriElement {

}
window.customElements.define('koy-datagrid-label', KoyoriDatagridLabel);

/**
 * セルの内容に文字列以外を表示する場合に指定します。
 */
class KoyoriDatagridContent extends KoyoriElement {

}
window.customElements.define('koy-datagrid-content', KoyoriDatagridContent);
